import { useState, useRef, useEffect } from "react";
import { UploadBox, StatusCard, SuccessCard, ErrorCard, Toast } from "./components/shared/WorkflowComponents";
import "./App.css";
import env from '../../configs/envConfig';

const ALLOWED_REQUIREMENTS = [".pdf", ".doc", ".docx", ".txt", ".rtf"];
const ALLOWED_RESUMES = [".pdf", ".doc", ".docx", ".txt", ".rtf", ".zip"];
const API_URL = env.REACT_APP_RESUME_PARSER_API_URL ?? "https://localhost:8000";
const MAX_FILES  = 300;

const STEPS = [
  "Parsing requirements...",
  "Parsing resumes...",
  "Comparing candidates...",
  // "Checking for suspect resumes...",  // temporarily disabled
  "Generating summary...",
  "Building Excel report...",
];

export default function ResumeParser() {
  const [reqFile, setReqFile]           = useState(null);
  const [resumeFiles, setResumeFiles]   = useState([]);
  const [status, setStatus]             = useState("idle");
  const [stepIdx, setStepIdx]           = useState(0);
  const [downloadUrl, setDownloadUrl]   = useState(null);
  const [downloadName, setDownloadName] = useState("");
  const [errorMsg, setErrorMsg]         = useState("");
  const [toast, setToast]               = useState(null);
  const [jobId, setJobId]               = useState(null);
  const pollingRef                      = useRef(null);

  const showToast = (msg) => {
    setToast(msg);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  // Clean up polling on unmount
  useEffect(() => () => {
    stopPolling();
  }, []);

  const handleRun = async () => {
    if (!reqFile || resumeFiles.length === 0) return;
    setStatus("running");
    setStepIdx(0);
    setDownloadUrl(null);
    setErrorMsg("");
    setJobId(null);
    setToast(null);

    try {
      const formData = new FormData();
      formData.append("requirements", reqFile);
      resumeFiles.forEach(file => formData.append("resumes", file));

      const res = await fetch(`${API_URL}/parse/async`, { method: "POST", body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}: ${res.statusText}` }));
        throw new Error(`Failed to submit job: ${err.detail || res.statusText} (Status: ${res.status})`);
      }

      const data = await res.json();
      const newJobId = data.job_id;
      setJobId(newJobId);

      // Start polling for status updates
      let pollErrorCount = 0;
      pollingRef.current = setInterval(async () => {
        try {
          const statusRes = await fetch(`${API_URL}/status/${newJobId}`);

          if (!statusRes.ok) {
            console.error(`Status check failed: ${statusRes.status} ${statusRes.statusText}`);
            // Handle HTTP errors
            if (statusRes.status === 404) {
              stopPolling();
              setErrorMsg(`Job not found. It may have been removed or expired.\n\nJob ID: ${newJobId}\nStatus: ${statusRes.status} ${statusRes.statusText}`);
              setStatus("error");
              return;
            } else if (statusRes.status >= 500) {
              pollErrorCount++;
              console.warn(`Server error ${statusRes.status}, attempt ${pollErrorCount}/5`);
              if (pollErrorCount >= 5) {
                stopPolling();
                setErrorMsg(`Unable to retrieve job status due to server errors (${statusRes.status} ${statusRes.statusText}). Please contact your administrator.\n\nJob ID: ${newJobId}`);
                setStatus("error");
              }
              return;
            }
            // For other errors, keep polling but log them
            console.warn(`Unexpected status code ${statusRes.status}, continuing to poll...`);
            return;
          }

          // Reset error count on success
          pollErrorCount = 0;

          const statusData = await statusRes.json();

          // Update step progress
          if (statusData.step !== undefined) {
            setStepIdx(statusData.step);
          }

          // Handle completion
          if (statusData.status === "completed") {
            stopPolling();
            setDownloadUrl(`${API_URL}/download/job/${newJobId}`);
            setDownloadName(statusData.filename || "resume_report.xlsx");
            setStepIdx(STEPS.length);
            setStatus("done");
          }

          // Handle errors
          if (statusData.status === "failed") {
            stopPolling();
            // Use specific error from backend if available
            const errorDetail = statusData.error || "An unknown error occurred during processing.";

            // Check if it's a timeout-related error
            const isTimeout = errorDetail.toLowerCase().includes('timeout') ||
                             errorDetail.toLowerCase().includes('timed out');

            // Provide detailed error message with job ID for debugging
            setErrorMsg(
              isTimeout
                ? "Processing timed out. This typically happens with large resume batches. Please try again with fewer resumes, or contact your administrator if the issue persists."
                : `Processing failed: ${errorDetail}\n\nJob ID: ${newJobId}\n\nIf this error persists, please contact your administrator with the Job ID.`
            );
            setStatus("error");
          }
        } catch (err) {
          console.error("Polling error:", err);
          pollErrorCount++;

          // Stop polling after too many consecutive network errors
          if (pollErrorCount >= 10) {
            stopPolling();
            setErrorMsg("Lost connection to server. Please check your network and try again.");
            setStatus("error");
          }
        }
      }, 2000); // Poll every 2 seconds

    } catch (err) {
      console.error('Job submission error:', err);
      const isTimeout =
        err.message?.toLowerCase().includes('network') ||
        err.message?.toLowerCase().includes('timeout') ||
        err.name === 'TypeError';


      let errorMessage;
      if (isTimeout) {
        errorMessage = 'Processing timed out. This typically happens with large resume batches. Please try again with fewer resumes, or contact your administrator if the issue persists.';
      } else if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        errorMessage = `Server endpoint not found (404). The API may not be running or the URL may be incorrect.\n\nAPI URL: ${API_URL}\n\nTechnical details: ${err.message}`;
      } else {
        errorMessage = err.message || 'An unexpected error occurred';
      }

      setErrorMsg(errorMessage);
      setStatus("error");
    }
  };

  const handleReset = async () => {
    stopPolling();

    // Clean up files from previous job if job_id exists
    if (jobId) {
      try {
        await fetch(`${API_URL}/cleanup/${jobId}`, { method: 'DELETE' });
      } catch (err) {
        // Cleanup failure is not critical, just log it
        console.warn('Failed to cleanup job files:', err);
      }
    }

    setStatus("idle");
    setReqFile(null);
    setResumeFiles([]);
    setDownloadUrl(null);
    setErrorMsg("");
    setStepIdx(0);
    setJobId(null);
  };

  const canRun = reqFile && resumeFiles.length > 0 && status !== "running";

  return (
      <div className="page">
        <div className="container">

          <div className="header">
            <h1>Resume Parser</h1>
            <p>Upload requirements and resumes, then run to generate a ranked Excel report</p>
          </div>

        {/* ── Upload boxes ── */}
        <div className="upload-row">
          <UploadBox
            label="📋 Job Requirements"
            icon="📋"
            multiple={false}
            limitHint="Limited to 1 requirement file"
            onFiles={([file]) => setReqFile(file)}
            onInvalid={showToast}
            allowed={ALLOWED_REQUIREMENTS}
            disabled={status === "running"}
          />
          <UploadBox
            label="👤 Resumes"
            icon="👤"
            multiple={true}
            limitHint={`Limit to ${MAX_FILES} resumes. Please split into multiple runs, if needed.`}
            onFiles={newFiles => setResumeFiles(prev => {
              const names = new Set(prev.map(file => file.name));
              const filtered = newFiles.filter(file => !names.has(file.name));
              const combined = [...prev, ...filtered];
              if (combined.length > MAX_FILES) {
                showToast(`File limit exceeded. You can upload a maximum of ${MAX_FILES} files. ${combined.length - MAX_FILES} were not added.`);
                return combined.slice(0, MAX_FILES);
              }
              return combined;
            })}
            onInvalid={showToast}
            allowed={ALLOWED_RESUMES}
            disabled={status === "running"}
          />
        </div>

        {/* ── Uploaded file badges ── */}
        <div className="upload-file-row">
          <div className="upload-column">
            {reqFile && (
              <div className="file-badge single">
                <span>📄 {reqFile.name}</span>
                <button onClick={() => setReqFile(null)}>×</button>
              </div>
            )}
          </div>
          <div className="upload-column">
            {resumeFiles.length > 0 && (
              <div className="resume-list">
                {resumeFiles.map(file => (
                  <div key={file.name} className="file-badge multi">
                    <span>📄 {file.name}</span>
                    <button onClick={() => setResumeFiles(prev => prev.filter(existingFile => existingFile.name !== file.name))}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Run button — hidden once done ── */}
        {status !== "done" && (
          <div style={{ textAlign: "center", marginTop: "8px" }}>
            <button
              onClick={handleRun}
              disabled={!canRun}
              className={`run-btn ${canRun ? "ready" : "disabled"}`}
            >
              {status === "running" ? "Running..." : "Run Parser"}
            </button>
          </div>
        )}

        {/* ── Hint text — guides user on what to upload next ── */}
        {status === "idle" && (
          <p className="hint">
            {!reqFile && resumeFiles.length === 0
              ? "Upload a requirements file and at least one resume to begin"
              : !reqFile
              ? "Upload a requirements file to begin"
              : resumeFiles.length === 0
              ? "Upload at least one resume to begin"
              : "Press Run Parser to generate the comparison report"}
          </p>
        )}

        {/* ── AI Disclaimer ── */}
        <p className="ai-disclaimer">
          ⚠️ Scores are AI-generated and may not reflect all relevant experience. Human review is recommended before advancing or eliminating candidates.
        </p>

        {/* ── Toast notification for invalid file types ── */}
        <Toast message={toast} onClose={() => setToast(null)} />

        {/* ── Status card — shows live step progress while processing ── */}
        {status === "running" && (
          <StatusCard steps={STEPS} currentStep={stepIdx} />
        )}

        {/* ── Success card — shown when report is ready to download ── */}
        {status === "done" && (
          <SuccessCard
            downloadUrl={downloadUrl}
            downloadName={downloadName}
            onReset={handleReset}
            onDownloadError={showToast}
          />
        )}

        {/* ── Error card — shown when processing fails ── */}
        {status === "error" && (
          <ErrorCard
            errorMsg={errorMsg}
            onRetry={() => setStatus("idle")}
          />
        )}

        </div>
      </div>
  );
}
